import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  Skeleton,
  Flex,
  useToast,
  ListItem,
  List,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState } from 'react';
import Select, {
  createFilter,
  GroupBase,
  Options,
  OptionsOrGroups,
} from 'react-select';
import { FixedSizeList } from 'react-window';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  ReceivableBorrowingBaseTestWaiverGQLType,
  ReceivableBorrowingBaseTestWaiverInputGQLType,
  ReceivableIdGroupWithLoanProductGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import {
  CreditAgreement,
  parseCreditAgreementFullArtifact,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

type Option = {
  value: string;
  label: string;
};

type WaiverTypeOption = {
  value: 'eligibility' | 'concentration';
  label: string;
};

const createReceivableIdSelectOptions = (
  receivableIdGroups: ReceivableIdGroupWithLoanProductGQLType[],
) => {
  return _.map(receivableIdGroups, (receivable) => {
    const { finleyReceivableId, externalReceivableId } = receivable;

    return {
      value: finleyReceivableId,
      label: externalReceivableId,
    };
  });
};

const height = 35;

type MenuListPropType = {
  options: OptionsOrGroups<Option, GroupBase<Option>>;
  children: any;
  maxHeight: number;
  getValue: () => Options<Option>;
};

const MenuList = ({
  options,
  children,
  maxHeight,
  getValue,
}: MenuListPropType) => {
  const [value] = getValue();
  const initialOffset = options.indexOf(value) * height;

  return (
    // !TODO @eldarshamukhamedov This is the only place where react-window is used, and it can be replaced with standard Chakra select/menu dropdown component
    <FixedSizeList
      height={maxHeight}
      itemCount={children.length}
      itemSize={height}
      initialScrollOffset={initialOffset}
      width="inherit"
    >
      {({ index }) => <Box>{children[index]}</Box>}
    </FixedSizeList>
  );
};

type ReceivableIdMenuProps = {
  receivableIdGroups: ReceivableIdGroupWithLoanProductGQLType[];
  selectedReceivableOption: Option | null;
  setSelectedReceivableOption: React.Dispatch<
    React.SetStateAction<Option | null>
  >;
};
const ReceivableIdMenu = ({
  receivableIdGroups,
  selectedReceivableOption,
  setSelectedReceivableOption,
}: ReceivableIdMenuProps) => {
  const options = createReceivableIdSelectOptions(receivableIdGroups);

  if (receivableIdGroups.length === 0) {
    return null;
  }

  const onSelectOption = (receivableOption: Option) => {
    setSelectedReceivableOption(receivableOption);
  };

  return (
    <Box>
      <Select
        components={{ MenuList }}
        value={selectedReceivableOption}
        placeholder="Enter Receivable ID"
        onChange={(newValue) => {
          const newVal = newValue as { value: string; label: string };
          onSelectOption(newVal);
        }}
        filterOption={createFilter({ ignoreAccents: false })}
        options={options}
      />
    </Box>
  );
};

const waiverTypeOptions: WaiverTypeOption[] = [
  {
    value: 'eligibility',
    label: 'Eligibility Criteria',
  },
  { value: 'concentration', label: 'Concentration Limit' },
];

type WaiverTypeMenuProps = {
  selectedWaiverTypeOption: WaiverTypeOption | null;
  setSelectedWaiverTypeOption: React.Dispatch<
    React.SetStateAction<WaiverTypeOption | null>
  >;
};
const WaiverTypeMenu = ({
  selectedWaiverTypeOption,
  setSelectedWaiverTypeOption,
}: WaiverTypeMenuProps) => {
  const onSelectOption = (borrowingBaseTestTypeOption: WaiverTypeOption) => {
    setSelectedWaiverTypeOption(borrowingBaseTestTypeOption);
  };

  return (
    <Box>
      <Select
        value={selectedWaiverTypeOption}
        isClearable
        isSearchable
        placeholder="Choose waiver type"
        onChange={(newValue) => {
          const newVal = newValue as WaiverTypeOption;
          onSelectOption(newVal);
        }}
        options={waiverTypeOptions}
        filterOption={createFilter({ ignoreAccents: false })}
      />
    </Box>
  );
};

const getLoanProductBorrowingBaseTestPropertyKeys = (
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
  loanProductId: string,
  type: 'eligibility' | 'concentration',
): Option[] => {
  const loanProductMechanics =
    creditAgreement.borrowingBaseMechanics.calcAgentMechanics
      .loanProductMechanics;
  const loanProductMechanicsForLoanProduct = _.find(
    loanProductMechanics,
    (loanProductMechanic) => loanProductMechanic.entity.id === loanProductId,
  );
  if (!loanProductMechanicsForLoanProduct) {
    return [];
  }

  const eligibilityTestPropertyKeys = _.compact(
    _.map(
      loanProductMechanicsForLoanProduct.eligibilityTests,
      (eligibilityTest) => {
        const propertyKey =
          eligibilityTest._config_?.eligibilityTestConfig?.propertyKey;
        return propertyKey
          ? {
              value: propertyKey,
              label: commonUtils.toTitleCase(propertyKey),
            }
          : undefined;
      },
    ),
  );

  const concentrationLimitTestPropertyKeys = _.compact(
    _.map(
      loanProductMechanicsForLoanProduct.concentrationLimits,
      (concentrationLimit) => {
        const propertyKey =
          concentrationLimit._config_?.concentrationLimitConfig?.propertyKey;
        return propertyKey
          ? {
              value: propertyKey,
              label: commonUtils.toTitleCase(propertyKey),
            }
          : undefined;
      },
    ),
  );

  const mlpConcentrationLimitPropertyKeys = _.compact(
    _.map(
      creditAgreement.borrowingBaseMechanics.calcAgentMechanics
        .multiLoanProductConcentrationLimits,
      (mlpConcLimit) => {
        const propertyKey =
          mlpConcLimit._config_?.concentrationLimitConfig?.propertyKey;
        return propertyKey
          ? {
              value: propertyKey,
              label: commonUtils.toTitleCase(propertyKey),
            }
          : undefined;
      },
    ),
  );

  switch (type) {
    case 'eligibility':
      return eligibilityTestPropertyKeys;
    case 'concentration':
      return [
        ...concentrationLimitTestPropertyKeys,
        ...mlpConcentrationLimitPropertyKeys,
      ];
    default:
      return [];
  }
};

type WaiverKeysMenuProps = {
  selectedWaiverKeyOption: Option | null;
  setSelectedWaiverKeyOption: React.Dispatch<
    React.SetStateAction<Option | null>
  >;
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  loanProductId: string;
  type: 'eligibility' | 'concentration';
};
export const WaiverKeysMenu = ({
  selectedWaiverKeyOption,
  setSelectedWaiverKeyOption,
  creditAgreement,
  loanProductId,
  type,
}: WaiverKeysMenuProps) => {
  const onSelectOption = (waiverKeyOption: Option) => {
    setSelectedWaiverKeyOption(waiverKeyOption);
  };

  const waiverKeyOptions = getLoanProductBorrowingBaseTestPropertyKeys(
    creditAgreement,
    loanProductId,
    type,
  );

  return (
    <Box>
      <Select
        value={selectedWaiverKeyOption}
        isClearable
        isSearchable
        placeholder="Choose test"
        onChange={(newValue) => {
          const newVal = newValue as { value: string; label: string };
          onSelectOption(newVal);
        }}
        options={waiverKeyOptions}
      />
    </Box>
  );
};

type AddReceivableWaiverModalProps = {
  isOpen: boolean;
  onClose: () => void;
  receivableIdGroups: ReceivableIdGroupWithLoanProductGQLType[];
  creditFacilityId: string;
  loanProductId: string;
  queuedReceivableWaiverUpdates: ReceivableBorrowingBaseTestWaiverInputGQLType[];
  setQueuedReceivableWaiverUpdates: React.Dispatch<
    React.SetStateAction<ReceivableBorrowingBaseTestWaiverInputGQLType[]>
  >;
  queuedReceivableWaiverDeletions: ReceivableBorrowingBaseTestWaiverInputGQLType[];
  setQueuedReceivableWaiverDeletions: React.Dispatch<
    React.SetStateAction<ReceivableBorrowingBaseTestWaiverInputGQLType[]>
  >;
  activeReceivableWaivers: ReceivableBorrowingBaseTestWaiverGQLType[];
  preSelectedReceivableOptions?: ReceivableIdGroupWithLoanProductGQLType[]; // can pass in a list of pre-selected receivable ids in the select&update receivables mode. do not pass in for the global add waivers mode
};
export const AddReceivableWaiverModal = ({
  isOpen,
  onClose,
  receivableIdGroups,
  creditFacilityId,
  loanProductId,
  queuedReceivableWaiverUpdates,
  setQueuedReceivableWaiverUpdates,
  queuedReceivableWaiverDeletions,
  setQueuedReceivableWaiverDeletions,
  activeReceivableWaivers,
  preSelectedReceivableOptions,
}: AddReceivableWaiverModalProps) => {
  const toast = useToast();

  const [
    selectedReceivableOption,
    setSelectedReceivableOption,
  ] = useState<Option | null>(null);
  const [
    selectedWaiverTypeOption,
    setSelectedWaiverTypeOption,
  ] = useState<WaiverTypeOption | null>(null);
  const [
    selectedWaiverKeyOption,
    setSelectedWaiverKeyOption,
  ] = useState<Option | null>(null);
  const [
    selectedStartDate,
    setSelectedStartDate,
  ] = useState<Util.FullDateStr | null>(null);
  const [
    selectedExpirationDate,
    setSelectedExpirationDate,
  ] = useState<Util.FullDateStr | null>(null);

  const {
    loading: isCreditAgreementLoading,
    data: creditAgreementData,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
    },
  });

  if (!creditAgreementData || isCreditAgreementLoading) {
    return <Skeleton />;
  }

  const creditAgreement = parseCreditAgreementFullArtifact(
    creditAgreementData?.currentCreditAgreementFullArtifact?.creditAgreement,
  );

  if (!creditAgreement) {
    return <Text>A current Credit Agreement must exist to add waivers.</Text>;
  }

  const onCloseModal = () => {
    setSelectedReceivableOption(null);
    setSelectedWaiverTypeOption(null);
    setSelectedWaiverKeyOption(null);
    setSelectedStartDate(null);
    setSelectedExpirationDate(null);

    onClose();
  };

  const onAddWaiver = () => {
    const newWaiverQueue = [...queuedReceivableWaiverUpdates];
    const newWaiversToAddToQueue: ReceivableBorrowingBaseTestWaiverInputGQLType[] = [];
    const newDeletionQueue = [...queuedReceivableWaiverDeletions];

    if (!preSelectedReceivableOptions || !preSelectedReceivableOptions.length) {
      if (
        !selectedReceivableOption ||
        !selectedWaiverTypeOption ||
        !selectedWaiverKeyOption
      ) {
        if (!toast.isActive(errorToastId)) {
          errorToastConfig.title = 'Please fill in all required fields';
          toast(errorToastConfig);
        }
        return;
      }

      const newWaiver: ReceivableBorrowingBaseTestWaiverInputGQLType = {
        finleyReceivableId: selectedReceivableOption.value,
        externalReceivableId: selectedReceivableOption.label,
        loanProductId,
        creditFacilityId,
        borrowingBaseTestType: selectedWaiverTypeOption.value,
        borrowingBaseTestKey: selectedWaiverKeyOption.value,
        waiverStartReportingDate: selectedStartDate,
        waiverExpirationReportingDate: selectedExpirationDate,
        isActive: true,
      };
      newWaiversToAddToQueue.push(newWaiver);
    } else {
      if (!selectedWaiverTypeOption || !selectedWaiverKeyOption) {
        if (!toast.isActive(errorToastId)) {
          errorToastConfig.title = 'Please fill in all required fields';
          toast(errorToastConfig);
        }
        return;
      }

      _.forEach(preSelectedReceivableOptions, (preSelectedReceivable) => {
        const newWaiver: ReceivableBorrowingBaseTestWaiverInputGQLType = {
          finleyReceivableId: preSelectedReceivable.finleyReceivableId,
          externalReceivableId: preSelectedReceivable.externalReceivableId,
          loanProductId,
          creditFacilityId,
          borrowingBaseTestType: selectedWaiverTypeOption.value,
          borrowingBaseTestKey: selectedWaiverKeyOption.value,
          waiverStartReportingDate: selectedStartDate,
          waiverExpirationReportingDate: selectedExpirationDate,
          isActive: true,
        };

        newWaiversToAddToQueue.push(newWaiver);
      });
    }

    _.forEach(newWaiversToAddToQueue, (newWaiverToAddToQueue) => {
      const {
        finleyReceivableId,
        borrowingBaseTestType,
        borrowingBaseTestKey,
      } = newWaiverToAddToQueue;
      // check if waiver is already in the queue
      const existingWaiverQueueIndex = _.findIndex(
        queuedReceivableWaiverUpdates,
        {
          finleyReceivableId,
          creditFacilityId,
          borrowingBaseTestType,
          borrowingBaseTestKey,
        },
      );
      // check if waiver is already in active waivers
      const existingActiveWaiverIndex = _.findIndex(activeReceivableWaivers, {
        finleyReceivableId,
        creditFacilityId,
        borrowingBaseTestType,
        borrowingBaseTestKey,
      });

      if (existingWaiverQueueIndex === -1 && existingActiveWaiverIndex === -1) {
        // if new waiver is not in active list or in queue
        // push to queue
        newWaiverQueue.push(newWaiverToAddToQueue);
      } else if (existingWaiverQueueIndex !== -1) {
        // if new waiver is in the existing queue
        // replace the item in the queue
        newWaiverQueue.splice(
          existingWaiverQueueIndex,
          1,
          newWaiverToAddToQueue,
        );
      } else if (existingActiveWaiverIndex !== -1) {
        // if new waiver is not in existing queue but is an existing active waiver
        // push to queue
        newWaiverQueue.push(newWaiverToAddToQueue);
      }

      const existingWaiverInDeletionQueueIndex = _.findIndex(
        queuedReceivableWaiverDeletions,
        {
          finleyReceivableId,
          creditFacilityId,
          borrowingBaseTestType,
          borrowingBaseTestKey,
        },
      );
      if (existingWaiverInDeletionQueueIndex !== -1) {
        // remove from deletion queue
        newDeletionQueue.splice(existingWaiverInDeletionQueueIndex, 1);
      }
    });

    setQueuedReceivableWaiverUpdates(newWaiverQueue);
    setQueuedReceivableWaiverDeletions(newDeletionQueue);

    onCloseModal();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Waiver To Queue</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!preSelectedReceivableOptions && (
              <>
                <Text fontWeight={500} mb="8px">
                  Receivable ID
                </Text>
                <ReceivableIdMenu
                  receivableIdGroups={receivableIdGroups}
                  selectedReceivableOption={selectedReceivableOption}
                  setSelectedReceivableOption={setSelectedReceivableOption}
                />
              </>
            )}
            {preSelectedReceivableOptions && (
              <>
                <Text fontWeight={500} mb="8px">
                  Receivable IDs
                </Text>
                <List>
                  {_.map(
                    preSelectedReceivableOptions,
                    (preSelectedReceivableOption) => (
                      <ListItem
                        key={`${preSelectedReceivableOption.finleyReceivableId}`}
                      >
                        {preSelectedReceivableOption.externalReceivableId}
                      </ListItem>
                    ),
                  )}
                </List>
              </>
            )}
            <Text fontWeight={500} mt="16px" mb="8px">
              Waiver Type
            </Text>
            <WaiverTypeMenu
              selectedWaiverTypeOption={selectedWaiverTypeOption}
              setSelectedWaiverTypeOption={setSelectedWaiverTypeOption}
            />
            {selectedWaiverTypeOption && (
              <>
                <Text fontWeight={500} mt="16px" mb="8px">
                  Test
                </Text>
                <WaiverKeysMenu
                  selectedWaiverKeyOption={selectedWaiverKeyOption}
                  setSelectedWaiverKeyOption={setSelectedWaiverKeyOption}
                  creditAgreement={creditAgreement}
                  loanProductId={loanProductId}
                  type={selectedWaiverTypeOption.value}
                />
              </>
            )}
            {selectedWaiverTypeOption && selectedWaiverKeyOption && (
              <>
                <Text fontWeight={500} mt="16px" mb="8px">
                  Waiver Start Date (optional)
                </Text>
                <Flex>
                  <SimpleDatePicker
                    width="100%"
                    dateFormat="MMMM dd, yyyy"
                    selectedDate={
                      selectedStartDate
                        ? commonDate.fullDateStrToDate(selectedStartDate)
                        : undefined
                    }
                    showTimeSelect={false}
                    setSelectedDate={(date: Date | null) =>
                      setSelectedStartDate(
                        date ? commonDate.dateToFullDateString(date) : null,
                      )
                    }
                    placeHolder="Select start date"
                    maxDate={
                      selectedExpirationDate
                        ? commonDate.fullDateStrToDate(selectedExpirationDate)
                        : undefined
                    }
                  />
                  <Button
                    variant="link"
                    onClick={() => setSelectedStartDate(null)}
                  >
                    <Icon icon="xmark" type="solid" color={colors.gray[400]} />
                  </Button>
                </Flex>
                <Text fontWeight={500} mt="16px" mb="8px">
                  Waiver Expiration Date (optional)
                </Text>
                <Flex>
                  <SimpleDatePicker
                    width="100%"
                    dateFormat="MMMM dd, yyyy"
                    selectedDate={
                      selectedExpirationDate
                        ? commonDate.fullDateStrToDate(selectedExpirationDate)
                        : undefined
                    }
                    showTimeSelect={false}
                    setSelectedDate={(date: Date | null) =>
                      setSelectedExpirationDate(
                        date ? commonDate.dateToFullDateString(date) : null,
                      )
                    }
                    placeHolder="Select expiration date"
                    minDate={
                      selectedStartDate
                        ? commonDate.fullDateStrToDate(selectedStartDate)
                        : undefined
                    }
                  />
                  <Button
                    variant="link"
                    onClick={() => setSelectedExpirationDate(null)}
                  >
                    <Icon icon="xmark" type="solid" color={colors.gray[400]} />
                  </Button>
                </Flex>
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="aurora"
              variant="outline"
              borderWidth="2px"
              mr="32px"
              onClick={onCloseModal}
            >
              Close
            </Button>
            <Button colorScheme="aurora" onClick={onAddWaiver}>
              Add Waiver
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
